import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiUser, FiLock, FiLogIn } from 'react-icons/fi';
import { getAzureLoginUrl, login, getMe, setToken, setUserProfile } from '../../utils/journalApi';

const inputStyle = {
  width: '100%', padding: '9px 11px 9px 34px',
  border: '1px solid #d8e3f0', borderRadius: 9,
  fontSize: '0.87rem', fontFamily: 'inherit', color: '#12213a',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

export default function JournalLogin({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  /* ── Azure AD: redirect to backend login URL ── */
  const handleMicrosoftLogin = () => {
    window.location.href = getAzureLoginUrl();
  };

  /* ── Username / password (local dev fallback) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { setError('Enter username and password.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await login(username.trim(), password);
      setToken(res.data.access_token);
      try { const me = await getMe(); setUserProfile(me.data); } catch (_) {}
      onLogin();
    } catch (err) {
      const s = err.response?.status;
      setError(s === 401 || s === 400 ? 'Incorrect username or password.' : 'Login failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background:
        'radial-gradient(70rem 30rem at 4% -6%, rgba(20,118,153,0.19), transparent 70%),' +
        'radial-gradient(46rem 28rem at 98% 12%, rgba(229,110,17,0.18), transparent 64%),' +
        'linear-gradient(160deg, #eff5ff 0%, #f5f8f2 46%, #fff9ef 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Manrope','Segoe UI',system-ui,sans-serif",
    }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        style={{
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(216,227,240,0.9)',
          borderRadius: 22,
          padding: '40px 36px',
          width: 390,
          boxShadow: '0 16px 48px rgba(13,38,74,0.14)',
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'linear-gradient(130deg,#0f4c81,#1976a0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px',
            boxShadow: '0 8px 24px rgba(15,76,129,0.26)',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: "'Space Grotesk',sans-serif", color: '#11355a', letterSpacing: '-0.02em' }}>
            Journal Posting
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.87rem', color: '#57708d' }}>
            Sign in to continue
          </p>
        </div>

        {/* Microsoft / Azure AD button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleMicrosoftLogin}
          disabled={loading}
          style={{
            width: '100%', padding: '11px 0', marginBottom: 18,
            background: '#fff', color: '#12213a',
            border: '1.5px solid #d8e3f0', borderRadius: 10,
            fontSize: '0.9rem', fontWeight: 700, fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            boxShadow: '0 2px 8px rgba(13,38,74,0.07)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          Sign in with SAVIC Account
        </motion.button>

        {/* OR divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1, height: 1, background: '#e4ecf5' }} />
          <span style={{ fontSize: '0.75rem', color: '#a0b0c4', fontWeight: 700 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: '#e4ecf5' }} />
        </div>

        {/* Username / password */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.73rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.45px', color: '#57708d', display: 'block', marginBottom: 5 }}>
              Username
            </label>
            <div style={{ position: 'relative' }}>
              <FiUser size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#57708d' }} />
              <input
                type="text" autoComplete="username" value={username}
                onChange={e => { setUsername(e.target.value); setError(''); }}
                placeholder="e.g. alice" style={inputStyle} disabled={loading}
                onFocus={e => { e.target.style.borderColor = '#0f4c81'; }}
                onBlur={e =>  { e.target.style.borderColor = '#d8e3f0'; }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: '0.73rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.45px', color: '#57708d', display: 'block', marginBottom: 5 }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <FiLock size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#57708d' }} />
              <input
                type="password" autoComplete="current-password" value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder="••••••••" style={inputStyle} disabled={loading}
                onFocus={e => { e.target.style.borderColor = '#0f4c81'; }}
                onBlur={e =>  { e.target.style.borderColor = '#d8e3f0'; }}
              />
            </div>
          </div>

          {error && (
            <div style={{ background: '#fff1ef', color: '#8d2115', border: '1px solid #f0c1bc', borderRadius: 9, padding: '9px 13px', fontSize: '0.85rem', fontWeight: 600, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              width: '100%', padding: '10px 0',
              background: loading ? '#7ba8c8' : 'linear-gradient(130deg,#0f4c81,#1976a0)',
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: '0.9rem', fontWeight: 700, fontFamily: 'inherit',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <FiLogIn size={15} />
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
